/**
 * Vertical Landing Page - For Content Creators
 */
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { 
  Video, Mic, FileText, Instagram, Youtube, Twitter,
  CheckCircle, ArrowRight, Gift, Star, Sparkles, Zap
} from 'lucide-react';

const USE_CASES = [
  {
    icon: Video,
    title: "Video Scripts",
    description: "YouTube scripts, TikTok hooks, and Reels captions that convert.",
  },
  {
    icon: FileText,
    title: "Blog to Social",
    description: "Turn one blog post into 10 social posts across all platforms.",
  },
  {
    icon: Mic,
    title: "Podcast Show Notes",
    description: "AI listens to your episode and writes timestamps, summaries, and clips.",
  },
  {
    icon: Instagram,
    title: "Instagram Captions",
    description: "Hooks, CTAs, and hashtags optimized for engagement.",
  },
  {
    icon: Twitter,
    title: "Twitter Threads",
    description: "Turn any topic into a viral thread with perfect pacing.",
  },
  {
    icon: Youtube,
    title: "Thumbnails & Titles",
    description: "A/B test titles and get thumbnail text suggestions that pop.",
  }
];

export default function ForCreators() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Hero */}
      <section className="pt-24 pb-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-pink-500/10 to-purple-500/10 border border-pink-500/20 mb-8">
              <Sparkles className="w-4 h-4 text-pink-400" />
              <span className="text-sm font-medium text-pink-300">
                Built for Content Creators
              </span>
            </div>

            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
              Create More.<br />
              <span className="bg-gradient-to-r from-pink-400 to-purple-400 bg-clip-text text-transparent">
                Burnout Less.
              </span>
            </h1>

            <p className="text-xl text-zinc-400 max-w-2xl mx-auto mb-8">
              AI that writes your scripts, captions, threads, and show notes. 
              <span className="text-pink-400 font-semibold"> 5 free tasks to start.</span>
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                onClick={() => navigate('/templates')}
                className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-400 hover:to-purple-400 text-white font-bold rounded-full px-8 py-7 text-lg"
              >
                Try Free — No Credit Card
                <Gift className="ml-2 w-5 h-5" />
              </Button>
            </div>

            <div className="flex items-center justify-center gap-6 mt-10 text-sm text-zinc-500">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-pink-500" />
                <span>Your voice, amplified</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-pink-500" />
                <span>Works with your style</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="w-4 h-4 text-pink-500" />
                <span>10x your output</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-20 px-6 bg-zinc-900/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">
              What Creators Use It For
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {USE_CASES.map((useCase, index) => (
              <motion.div
                key={useCase.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="bg-zinc-800/50 border border-zinc-700/50 rounded-2xl p-6 hover:border-pink-500/30 transition-all"
              >
                <div className="w-12 h-12 bg-pink-500/10 rounded-xl flex items-center justify-center mb-4">
                  <useCase.icon className="w-6 h-6 text-pink-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">{useCase.title}</h3>
                <p className="text-zinc-400 text-sm">{useCase.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* The Math */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-pink-500/20 rounded-3xl p-8 md:p-12">
            <h2 className="text-2xl font-bold text-white mb-6 text-center">
              The Creator Math
            </h2>
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-4xl font-black text-pink-400 mb-2">2 hrs</div>
                <div className="text-zinc-400">Average time writing a script</div>
              </div>
              <div>
                <div className="text-4xl font-black text-purple-400 mb-2">5 min</div>
                <div className="text-zinc-400">With TripleTC AI</div>
              </div>
              <div>
                <div className="text-4xl font-black text-white mb-2">$0.50</div>
                <div className="text-zinc-400">Per script</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">
            Your Audience Is Waiting.
          </h2>
          <p className="text-zinc-400 mb-8">
            Stop staring at blank pages. Start creating.
          </p>
          <Button
            size="lg"
            onClick={() => navigate('/auth?redirect=/templates')}
            className="bg-gradient-to-r from-pink-500 to-purple-500 hover:from-pink-400 hover:to-purple-400 text-white font-bold rounded-full px-10 py-7 text-lg"
          >
            Get 5 Free Tasks
            <Zap className="ml-2 w-5 h-5" />
          </Button>
        </div>
      </section>
    </div>
  );
}
